interface ViewFlagsSource {
    id: number;
    progressStatus?: number | string;
    isReadyForReview?: boolean;
    isComplete?: boolean;
}

/**
 * True when any view's status-bearing flags differ between the component's local
 * table copy and fresh props. DataTableForm keeps a local copy of the table (to
 * preserve freeze-panes settings and merged row versions) and refreshes it from
 * props only when rows/columns/table-status change — a mark-as-complete save
 * changes only VIEW-level flags, so without this check the stale local copy wins
 * until remount and the UI shows the pre-save status.
 */
export const haveViewFlagsDiverged = (
    localViews: ViewFlagsSource[] | undefined,
    propsViews: ViewFlagsSource[]
): boolean => {
    if (!localViews) {
        return false;
    }

    const flags = (views: ViewFlagsSource[]) =>
        JSON.stringify(
            views.map((view) => [
                view.id,
                view.progressStatus,
                view.isReadyForReview,
                view.isComplete,
            ])
        );

    return flags(localViews) !== flags(propsViews);
};

export interface MarkAsCompleteSyncResult {
    /** New checkbox value to apply (only when the user has no pending choice). */
    nextState?: boolean;
    /** True when the incoming data confirms the local value, so the latch can release. */
    releaseModifiedLatch: boolean;
}

/**
 * Decides how the Mark-as-Complete checkbox reconciles with incoming props data.
 *
 * The "modified" latch protects the user's (possibly just-saved) choice from being
 * overwritten by stale props: some hosts pass a no-op updateProjectsData and never
 * refetch after a save, and the admin dashboard's 10s polling can deliver a stale
 * response after a save. The latch therefore releases only when the incoming value
 * agrees with the local one — never on a timer or on save completion.
 */
export const syncMarkAsCompleteState = (
    propsValue: boolean | undefined,
    localValue: boolean,
    isModified: boolean
): MarkAsCompleteSyncResult => {
    if (!isModified) {
        return { nextState: propsValue, releaseModifiedLatch: false };
    }

    return { releaseModifiedLatch: propsValue === localValue };
};
